import _ from 'lodash';
import { Editor } from 'slate-react';
import PropTypes from 'prop-types';
import React, { useMemo, useRef } from 'react';

import FocusPlugin, { useFocusOnMount } from './plugins/focus';
import ReadOnlyPlugin from './plugins/read_only';
import SelectionPlugin from './plugins/selection';
import SubmitPlugin from './plugins/submit';
import TextEditorContext, { useTextEditorContextValue } from './text_editor_context';
import ToggleHidePlugin from './plugins/toggle_hide';
import { usePlaceholder } from './placeholder';
import useOnPaste from './paste/use_on_paste';
import Variables, { useVariables } from './plugins/variables';

export default function TextEditor(props) {
  let {
    autoFocus,
    onBlur,
    onChange,
    onSubmit,
    onToggleHide,
    placeholder,
    plugins,
    value,
    readOnly,
    renderNode,
  } = props;

  const placeholderElement = usePlaceholder(placeholder);

  const editorRef = useEditorRef(props.editorRef);
  useFocusOnMount(editorRef.current, onChange, autoFocus);
  useVariables(editorRef.current, onChange);

  const textEditorContextValue = useTextEditorContextValue();

  const onPaste = useOnPaste();
  const optionalProps = {};
  if (onPaste) {
    optionalProps.onPaste = onPaste;
  }

  const allPlugins = usePlugins(
    () => [
      ReadOnlyPlugin(readOnly),
      FocusPlugin(),
      SelectionPlugin(),
      SubmitPlugin(onSubmit),
      Variables(),
      ToggleHidePlugin(onToggleHide),
      ...plugins,
    ],
    [readOnly, ...plugins]
  );

  return (
    <TextEditorContext.Provider value={textEditorContextValue}>
      <Editor
        autoFocus={autoFocus}
        className="slateTextEditor"
        onBlur={onBlur}
        onChange={onChange}
        placeholder={placeholderElement}
        plugins={allPlugins}
        ref={editorRef}
        renderNode={renderNode}
        spellCheck
        value={value}
        {...optionalProps}
      />
    </TextEditorContext.Provider>
  );
}

TextEditor.defaultProps = {
  autoFocus: true,
  plugins: [],
  renderNode: (props, editor, next) => {
    return next();
  },
};

TextEditor.propTypes = {
  autoFocus: PropTypes.bool,
  className: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onToggleHide: PropTypes.func,
  onSubmit: PropTypes.func,
  placeholder: PropTypes.string,
  plugins: PropTypes.array,
  readOnly: PropTypes.bool,
  renderNode: PropTypes.func,
  submitOnEnter: PropTypes.bool,
  value: PropTypes.object.isRequired,
};

/**
 * Returns a cached copy of the return value of `pluginFunc`. We cache this so that Slate does not
 * yell at us about changing the `plugins` prop too frequently (as that is inefficient).
 */
export function usePlugins(pluginFunc, inputs = []) {
  return useMemo(pluginFunc, inputs);
}

export function useEditorRef(editorRef) {
  const ref = useRef(null);
  return editorRef ? editorRef : ref;
}

export function getEditorProps(props) {
  return _.pick(props, [
    'autoFocus',
    'className',
    'onBlur',
    'onChange',
    'onToggleHide',
    'onSubmit',
    'placeholder',
    'value',
    'readOnly',
  ]);
}



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/text_editor/text_editor.jsx